import { Dispatch } from 'redux'

import { Response } from 'cross-fetch'

import {
  FilteractionObject,
  FilterBody,
  ProjectFields,
  SortOption,
  YesObject
} from '@yes.technology/react-toolkit'

import { resolveQuery } from '../modules/filteraction/FilteractionResolver'

import apiAsync from './api-async'

import {
  FetchYesObjectsResponse,
  FilteractionParams,
  GenericAPIResponse,
  GetInteractionModelResponse,
  PostSignedUploadResponse,
  ReplaceFilteractionParamsParams
} from './apiTypes'

import fetchFilteraction from 'modules/filteraction/fetchFilteraction'
import slugify from 'slugify'

class Api {
  private dispatch!: Dispatch

  setDispatch = (dispatch: Dispatch) => {
    this.dispatch = dispatch
  }

  postInputValidation = (
    obj: unknown,
    endpoint: string,
    showLoading: boolean
  ): Promise<GenericAPIResponse> => {
    return apiAsync.requestSync({
      endpoint,
      method: 'POST',
      obj,
      showLoading,
      customEndpoint: true
    })
  }

  getAutocomplete = async (
    value: string,
    endpoint: string,
    showLoading: boolean,
    filteraction?: string,
    filteractionParams?: FilteractionParams[],
    throwError?: boolean,
    projectFields?: ProjectFields,
    sourceActivityValues?: Record<string, string | undefined>
  ): Promise<FetchYesObjectsResponse> => {
    if (filteraction) {
      return await this.getAutocompleteWithFilteraction(
        value,
        filteraction,
        showLoading,
        filteractionParams,
        throwError,
        projectFields,
        sourceActivityValues
      )
    }

    const response = await apiAsync.requestSync({
      endpoint: `${endpoint}?s=${encodeURIComponent(value)}`,
      method: 'GET',
      showLoading,
      customEndpoint: true
    })

    return {
      metadata: response.data?.metadata?.object_class,
      objects: response.data?.objects || response.data,
      pagination: response.data?.pagination,
      message: response.message
    }
  }

  replaceFilteractionParams = ({
    filteractionParams,
    filteraction,
    sourceActivityValues
  }: ReplaceFilteractionParamsParams) => {
    filteractionParams.forEach((param) => {
      const value = sourceActivityValues?.[param.input_value_activity_id]

      if (!value) return

      filteraction.query = filteraction.query.replace(
        param.query_param_name,
        value
      )
    })
  }

  getAutocompleteWithFilteraction = async (
    value: string,
    filteractionId: string,
    showLoading: boolean,
    filteractionParams?: FilteractionParams[],
    throwError?: boolean,
    projectFields?: ProjectFields,
    sourceActivityValues?: Record<string, string | undefined>
  ): Promise<FetchYesObjectsResponse> => {
    const onProgressCallback = () => undefined

    const filteraction = await fetchFilteraction(
      filteractionId,
      showLoading,
      throwError
    )

    if (filteractionParams && filteraction && sourceActivityValues) {
      // percorre todos os steps para encontrar activities com o mesmo UUID dos parâmetros da filteraction e executar replace das strings
      // todo: talvez no futuro aqui entre uma validação para activity_group
      this.replaceFilteractionParams({
        filteractionParams,
        sourceActivityValues,
        filteraction: filteraction as FilteractionObject
      })
    }

    const query =
      filteraction?.query && typeof filteraction.query === 'string'
        ? JSON.parse(filteraction.query)
        : {}

    const objectclassId = (
      filteraction?.filter_action_objectclass as YesObject[]
    )?.[0]?.uuid

    const filteractionResolvedQuery = await resolveQuery(
      query,
      onProgressCallback,
      [filteractionId],
      showLoading
    )

    // Agora juntamos os IDs retornados pela filteraction com
    // a descrição digitada no autocomplete
    const autocompleteQuery = {
      $and: [
        filteractionResolvedQuery,
        {
          'des.des': { $regex: value }
        }
      ]
    }

    const filterBody = {
      query: autocompleteQuery,
      objectclass: objectclassId,
      orderby: [
        {
          'des.des': 'asc'
        } as SortOption
      ],
      pagination: {
        offset: 0,
        limit: 100
      },
      visualization: 'classic-list',
      skip_relations: !projectFields,
      skip_metadata: true,
      project_fields: projectFields || { uuid: 1, des: 1 }
    }

    const results = this.postObjectsFilter(filterBody, showLoading, throwError)

    return results
  }

  async postObjectsFilter<T extends YesObject>(
    obj: FilterBody,
    showLoading = true,
    throwError = true
  ): Promise<FetchYesObjectsResponse<T>> {
    const response = await apiAsync.requestSync({
      endpoint: 'objects_filter',
      method: 'POST',
      obj,
      showLoading,
      throwError
    })

    return {
      metadata: response.data?.metadata?.object_class,
      objects: response.data?.objects as T[],
      pagination: response.data?.pagination
    }
  }

  postReloadToken = (obj: unknown, showLoading?: boolean) => {
    return apiAsync.requestSync({
      endpoint: 'reload-token',
      method: 'POST',
      obj,
      showLoading
    })
  }

  postSaveInteraction = async (obj: unknown, showLoading: boolean) => {
    return apiAsync
      .requestSync({
        endpoint: 'interactions',
        method: 'POST',
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })

          throw error
        }
      })
  }

  patchSaveInteraction = async (obj: unknown, showLoading: boolean) => {
    return apiAsync
      .requestSync({
        endpoint: 'interactions',
        method: 'PATCH',
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })

          throw error
        }
      })
  }

  getInteractionModels = async (
    idInteractionModel: string,
    showLoading: boolean
  ): Promise<GetInteractionModelResponse | null> => {
    return apiAsync
      .requestSync({
        endpoint: 'interaction-models',
        method: 'GET',
        path: '/' + idInteractionModel,
        showLoading
      })
      .then((response: GenericAPIResponse) => {
        if (response?.success && response?.data) {
          return response
        }

        throw new Error(response?.message)
      })
      .catch((e: unknown) => {
        if (
          e instanceof Error &&
          e?.message.includes('interaction model not found')
        ) {
          return null
        }

        throw new Error(e instanceof Error ? e.message : 'Erro desconhecido')
      })
  }

  async patchObjects<T extends YesObject = YesObject>(
    obj: Partial<T>,
    uuid: string,
    showLoading: boolean
  ): Promise<T> {
    const response = await apiAsync
      .requestSync({
        endpoint: 'objects',
        method: 'PATCH',
        path: '/' + uuid,
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })
        }
      })

    return response.data
  }

  async postObjects<T extends YesObject = YesObject>(
    obj: Partial<T>,
    showLoading: boolean
  ): Promise<T> {
    const response = await apiAsync
      .requestSync({
        endpoint: 'objects',
        method: 'POST',
        obj,
        showLoading,
        throwError: true
      })
      .catch((error: unknown) => {
        if (error instanceof Response) {
          this.dispatch({ type: 'api/ERROR', data: { status: error.status } })
        }
      })

    return response.data
  }

  postSignedUploadUrl = (
    obj: { filename: string; content_type: string },
    showLoading: boolean
  ): Promise<PostSignedUploadResponse> => {
    return apiAsync.requestSync({
      endpoint: 'signed-upload-url',
      method: 'POST',
      obj: {
        ...obj,
        filename: slugify(obj.filename)
          .toLowerCase()
          .replace(/[^a-z0-9-.]/g, '')
      },
      showLoading
    })
  }
}

export default new Api()
